import * as THREE from 'three';
import {
  MAX_INTERIOR_BOXES, INTERIOR_LIMITS, createInteriorBox, duplicateInteriorBox,
  interiorBoxMatrix, sanitizeVehicleInteriors,
} from './vehicleInteriors.js';
import './vehicleInteriorEditor.css';

export class VehicleInteriorEditor {
  constructor(calibrator) {
    this.calibrator = calibrator;
    this.selectedId = null;
    this.root = document.createElement('details');
    this.root.className = 'calibrator-section vehicle-interior-editor';
    this.root.innerHTML = `
      <summary><span>+</span> Chasis e interiores</summary>
      <div class="calibrator-fields">
        <p class="interior-note">Cajas visuales para tapar huecos del GLB. No modifican las colisiones. Se guardan con el auto y se ven también en el juego.</p>
        <label class="calibrator-check"><input type="checkbox" data-interior-enabled /><span>Mostrar interiores</span></label>
        <label><span>Caja seleccionada <output data-interior-count></output></span><select data-interior-select aria-label="Caja interior seleccionada"></select></label>
        <div class="interior-actions">
          <button type="button" data-interior-action="add">Agregar caja</button>
          <button type="button" data-interior-action="duplicate">Duplicar</button>
          <button type="button" data-interior-action="mirror">Duplicar al otro lado</button>
          <button type="button" data-interior-action="delete">Eliminar caja</button>
        </div>
        <p class="interior-note" data-interior-empty>Agregá una caja y ajustala dentro de la carrocería para cerrar el hueco.</p>
        <div class="interior-properties" hidden>
          <label><span>Nombre</span><input type="text" maxlength="80" data-interior-field="name" aria-label="Nombre de la caja" /></label>
          <div class="interior-appearance">
            <label class="calibrator-check"><input type="checkbox" data-interior-field="enabled" /><span>Activar esta caja</span></label>
            <label><span>Color mate</span><input type="color" data-interior-field="color" aria-label="Color de la caja" /></label>
          </div>
          ${Object.entries(INTERIOR_LIMITS).map(([property, axes]) => `
            <fieldset><legend>${{ position: 'Posición', size: 'Tamaño', rotationDegrees: 'Rotación (°)' }[property]}</legend>
            <div class="interior-vector">${Object.entries(axes).map(([axis, [min, max]]) => `
              <label><span>${property === 'size' ? { x: 'Ancho X', y: 'Alto Y', z: 'Largo Z' }[axis] : axis.toUpperCase()}</span>
              <input type="number" min="${min}" max="${max}" step="${property === 'rotationDegrees' ? 1 : .01}" data-interior-field="${property}.${axis}" aria-label="${{ position: 'Posición', size: 'Tamaño', rotationDegrees: 'Rotación' }[property]} ${axis.toUpperCase()}" /></label>
            `).join('')}</div></fieldset>
          `).join('')}
          <label class="calibrator-check"><input type="checkbox" data-interior-highlight checked /><span>Resaltar caja seleccionada</span></label>
          <p class="interior-note">X: lateral · Y: altura · Z: largo. Las cajas acompañan la escala y orientación de la carrocería. Podés bajar su opacidad para ver dónde quedan.</p>
        </div>
      </div>`;
    calibrator.screen.querySelector('[data-profile="body.scale"]').closest('details').after(this.root);
    this.select = this.root.querySelector('[data-interior-select]');
    const guideBox = new THREE.BoxGeometry(1, 1, 1);
    this.guide = new THREE.LineSegments(
      new THREE.EdgesGeometry(guideBox),
      new THREE.LineBasicMaterial({ color: 0x53f4ef, depthTest: false, transparent: true, opacity: .85 }),
    );
    guideBox.dispose();
    this.guide.name = 'selected_interior_box_guide';
    this.guide.matrixAutoUpdate = false;
    this.guide.renderOrder = 100;
    calibrator.guideGroup.add(this.guide);
    this.root.addEventListener('toggle', () => this.updateGuide());
    this.select.addEventListener('change', () => { this.selectedId = this.select.value; this.sync(); });
    this.root.querySelector('[data-interior-highlight]').addEventListener('change', () => this.updateGuide());
    this.root.querySelector('[data-interior-enabled]').addEventListener('change', (event) => {
      this.data.enabled = event.target.checked;
      this.changed();
    });
    this.root.querySelectorAll('[data-interior-action]').forEach((button) => {
      button.addEventListener('click', () => this.performAction(button.dataset.interiorAction));
    });
    this.root.querySelectorAll('[data-interior-field]').forEach((control) => {
      control.addEventListener('input', () => this.edit(control));
      // Allow intermediate empty/negative inputs while typing; restore a valid value on blur.
      control.addEventListener('change', () => this.sync());
    });
    this.sync();
  }

  get data() { return this.calibrator.profile.body.interiors; }
  get selected() { return this.data.boxes.find((box) => box.id === this.selectedId); }

  changed() {
    this.calibrator.applyProfile();
    const opacity = this.calibrator.screen.querySelector('#calibrator-body-opacity');
    if (opacity) this.calibrator.setBodyOpacity(Number(opacity.value));
  }

  performAction(action) {
    const box = this.selected;
    if (action === 'delete') {
      if (!box) return;
      this.data.boxes = this.data.boxes.filter((entry) => entry.id !== box.id);
    } else {
      if (this.data.boxes.length >= MAX_INTERIOR_BOXES || (action !== 'add' && !box)) return;
      const next = action === 'add' ? createInteriorBox() : duplicateInteriorBox(box, action === 'mirror');
      this.data.boxes.push(next);
      this.selectedId = next.id;
    }
    this.sync();
    this.changed();
  }

  edit(control) {
    const box = this.selected;
    if (!box) return;
    const [property, axis] = control.dataset.interiorField.split('.');
    if (axis) {
      if (!Number.isFinite(control.valueAsNumber)) return;
      const [min, max] = INTERIOR_LIMITS[property][axis];
      box[property][axis] = THREE.MathUtils.clamp(control.valueAsNumber, min, max);
    } else box[property] = control.type === 'checkbox' ? control.checked : control.value;
    const option = this.select.selectedOptions[0];
    if (option) option.textContent = this.boxLabel(box, this.select.selectedIndex);
    this.changed();
  }

  boxLabel(box, index) { return `${index + 1}. ${box.name || 'Caja interior'}${box.enabled ? '' : ' (desactivada)'}`; }

  sync() {
    this.calibrator.profile.body.interiors = sanitizeVehicleInteriors(this.data);
    if (!this.selected) this.selectedId = this.data.boxes[0]?.id || null;
    this.select.replaceChildren(...this.data.boxes.map((box, index) => new Option(this.boxLabel(box, index), box.id)));
    this.select.value = this.selectedId || '';
    this.select.disabled = !this.selected;
    this.root.querySelector('[data-interior-count]').textContent = `${this.data.boxes.length} / ${MAX_INTERIOR_BOXES}`;
    this.root.querySelector('[data-interior-enabled]').checked = this.data.enabled;
    this.root.querySelector('[data-interior-empty]').hidden = Boolean(this.selected);
    this.root.querySelector('.interior-properties').hidden = !this.selected;
    this.root.querySelectorAll('[data-interior-action]').forEach((button) => {
      const action = button.dataset.interiorAction;
      button.disabled = (action !== 'add' && !this.selected) || (action !== 'delete' && this.data.boxes.length >= MAX_INTERIOR_BOXES);
    });
    if (this.selected) this.root.querySelectorAll('[data-interior-field]').forEach((control) => {
      const [property, axis] = control.dataset.interiorField.split('.');
      const value = axis ? this.selected[property][axis] : this.selected[property];
      if (control.type === 'checkbox') control.checked = value;
      else control.value = value;
    });
    this.updateGuide();
  }

  updateGuide() {
    const box = this.selected;
    this.guide.visible = Boolean(this.root.open && this.data.enabled && box?.enabled
      && this.calibrator.rig.bodyMount.visible && this.root.querySelector('[data-interior-highlight]').checked);
    if (!this.guide.visible) return;
    this.calibrator.rig.bodyMount.updateWorldMatrix(true, false);
    this.calibrator.guideGroup.updateWorldMatrix(true, false);
    this.guide.matrix.copy(this.calibrator.guideGroup.matrixWorld).invert()
      .multiply(this.calibrator.rig.bodyMount.matrixWorld).multiply(interiorBoxMatrix(box));
    this.guide.matrixWorldNeedsUpdate = true;
  }

  dispose() {
    this.guide.removeFromParent();
    this.guide.geometry.dispose();
    this.guide.material.dispose();
    this.root.remove();
  }
}
